import { CircleAlert, Lock, LockOpen, MoveRight, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError, dashboardApi } from "../api";
import { useT } from "../i18n";
import { isManager, isSuperadmin } from "../roles";
import type { MonthClose, SessionUser } from "../types";
import { formatCredits, formatDate, monthLabel } from "../utils";
import Amount from "./Amount";

/**
 * Closing a month (BM ruling, 2026-08-20).
 *
 * Rollover used to be a number the system recomputed on every read. It is now a
 * number a MANAGER approves — and the point of this panel is that the manager sees
 * the figure BEFORE approving it, not afterwards.
 *
 * It lives on the statement because the statement is the month's closing document.
 * Sending somebody elsewhere to sign off a figure they are looking at is how a
 * feature ends up unused, which is what happened when the rate editor shipped
 * API-only.
 *
 * THE CLIENT SEES THE BADGE, NOT THE CONTROLS. "This month is closed and 60.5
 * credits carried" is the client's business — it is their money and their month.
 * CLOSING is manager-only and REOPENING is super-admin only; the server refuses
 * both regardless of what this file renders. Hiding a button is not a permission.
 */
export default function MonthClosePanel({
  month,
  user,
  onChanged,
}: {
  month: string;
  user: SessionUser;
  /** The ledger moves when a month closes, so the workspace must reload. */
  onChanged: () => void;
}) {
  const t = useT();
  const manager = isManager(user.role);
  // Reopening is a narrower door than closing (BM, 2026-08-20): it unfreezes a
  // figure the client may already have been shown.
  const superadmin = isSuperadmin(user.role);
  const [close, setClose] = useState<MonthClose | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      setClose((await dashboardApi.monthClose(month)).close);
    } catch (error) {
      // The preview is open to everyone on the account, so a 403 here means the
      // session is not on this account at all. Nothing to shout about — it means
      // this panel has nothing to say, not that something broke.
      if (error instanceof ApiError && error.status === 403) { setDenied(true); return; }
      toast.error(error instanceof Error ? error.message : t("error.readMonth"));
    }
  }, [month]);

  useEffect(() => { setConfirming(false); setNote(""); void load(); }, [load]);

  async function submit() {
    setBusy(true);
    try {
      const result = await dashboardApi.closeMonth(month, note);
      setClose(result.close);
      setConfirming(false);
      toast.success(t("toast.monthClosed", { month: monthLabel(month), credits: formatCredits(result.close.rollover) }));
      onChanged();
    } catch (error) {
      // The server sends the refusal WITH the preview, so the list of blocking work
      // updates in place rather than leaving a stale "ready to close" on screen.
      if (error instanceof ApiError) await load();
      toast.error(error instanceof Error ? error.message : t("error.closeMonth"));
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    setBusy(true);
    try {
      setClose((await dashboardApi.reopenMonth(month)).close);
      toast.success(`${monthLabel(month)} reopened`);
      onChanged();
    } catch (error) {
      if (error instanceof ApiError) await load();
      toast.error(error instanceof Error ? error.message : t("error.reopenMonth"));
    } finally {
      setBusy(false);
    }
  }

  if (denied || !close) return null;
  // A month outside the contract has no close to talk about.
  if (close.blockedReason === "not_in_contract") return null;

  return (
    <section className={`panel month-close${close.closed ? " month-close-done" : ""}`}>
      <div className="panel-heading">
        <div className="panel-heading-copy">
          {close.closed ? <ShieldCheck size={18} /> : <Lock size={18} />}
          <div>
            <h3>
              {t("close.title")}
              {close.closed && <span className="closed-badge">{t("close.closedBadge")}</span>}
            </h3>
            <p>
              {close.closed
                ? t("close.carried", { n: formatCredits(close.rollover) })
                : t("close.intro")}
            </p>
          </div>
        </div>
      </div>

      <div className="close-figures">
        <Figure label={t("close.pool")} value={close.balance.totalPool} />
        <Figure label={t("close.spent")} value={close.balance.spent} />
        <Figure label={t("close.committed")} value={close.balance.reserved} />
        {/* The number being approved. Given its own emphasis because it is the one
            the manager is signing off, not one of four equal facts. */}
        <Figure label={t("close.willCarry")} value={close.rollover} primary />
      </div>

      {!close.closed && (
        <p className="record-meta">{t("close.provisional")}</p>
      )}

      {close.closed && close.closedAt && (
        <p className="record-meta">
          {t("close.closedOn", { date: formatDate(close.closedAt), name: close.closedBy || "—" })}
          {close.note ? ` · ${close.note}` : ""}
        </p>
      )}

      {/* Named, not counted. Rebilling somebody's jobs to a different month is not
          a thing to do out of sight, and "3 requests will move" tells a manager
          nothing they can check. */}
      {!close.closed && close.moving.length > 0 && (
        <div className={`close-blocking${close.movingTo ? " close-moving" : ""}`}>
          {close.movingTo ? <MoveRight size={17} /> : <CircleAlert size={17} />}
          <div>
            <strong>
              {close.movingTo
                ? t("close.movingWork", { to: monthLabel(close.movingTo) })
                : close.blockedReason === "next_month_closed"
                  ? t("close.blockedNextClosed", { month: monthLabel(nextOf(month)) })
                  : t("close.blockedNowhere")}
            </strong>
            <ul>
              {close.moving.map((row) => (
                <li key={row.id}>
                  <span dir="auto">{row.title}</span>
                  <em><Amount value={row.credits} unit={t("common.cr")} /></em>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {!close.closed && close.blockedReason === "previous_month_open" && (
        <div className="close-blocking"><CircleAlert size={17} /><div><strong>{t("close.blockedPrevious")}</strong></div></div>
      )}

      {close.closed && close.reopenBlockedBy && (
        <div className="close-blocking">
          <CircleAlert size={17} />
          <div><strong>{t("close.reopenBlocked", { month: monthLabel(close.reopenBlockedBy) })}</strong></div>
        </div>
      )}

      {/* Manager only. The server enforces this at every amount — the same wall
          that guards creating credits, because a close moves money forward. */}
      {manager && !close.closed && close.canClose && !confirming && (
        <div className="close-actions">
          <button type="button" className="button" onClick={() => setConfirming(true)}>
            <Lock size={16} /> {t("close.action", { month: monthLabel(month) })}
          </button>
        </div>
      )}

      {manager && confirming && (
        <div className="close-confirm">
          <p>
            {t("close.confirmBody", { n: formatCredits(close.rollover), month: monthLabel(month) })}
            {close.moving.length > 0 && close.movingTo
              ? ` ${t("close.confirmMoving", { n: close.moving.length, to: monthLabel(close.movingTo) })}`
              : ""}
          </p>
          <label className="form-field">
            <span>{t("close.noteLabel")}</span>
            <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={500} />
          </label>
          <div className="close-actions">
            <button type="button" className="button" disabled={busy} onClick={() => void submit()}>
              {t("close.confirm")}
            </button>
            <button type="button" className="button button-secondary" disabled={busy} onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {close.closed && !close.reopenBlockedBy && (
        superadmin ? (
          <div className="close-actions">
            <button type="button" className="button button-secondary" disabled={busy} onClick={() => void reopen()}>
              <LockOpen size={16} /> {t("close.reopen", { month: monthLabel(month) })}
            </button>
          </div>
        ) : manager ? (
          // A manager who can close but not reopen should be told why the button is
          // absent, not left wondering whether it failed to render.
          <p className="record-meta">{t("close.reopenSuperadmin")}</p>
        ) : null
      )}
    </section>
  );
}

function Figure({ label, value, primary = false }: { label: string; value: number; primary?: boolean }) {
  const t = useT();
  return (
    <div className={`close-figure${primary ? " close-figure-primary" : ""}`}>
      <span>{label}</span>
      <strong><Amount value={value} unit={t("common.cr")} /></strong>
    </div>
  );
}

/** The month after this one, for naming where work would have gone. */
function nextOf(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  return monthNumber === 12 ? `${year + 1}-01` : `${year}-${String(monthNumber + 1).padStart(2, "0")}`;
}
